import { useMemo, useState } from 'react';
import {
    Button,
    ButtonProps,
    makeStyles,
    mergeClasses,
    Spinner,
    SpinnerProps,
    Text,
    TextProps,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import { CheckmarkFilled, DismissFilled, PlayFilled, StopFilled } from '@fluentui/react-icons';
import { useNavigate } from 'react-router';
import { Case, Task, TaskStatus, TimeTrackingEntry } from '../../data/types';
import { useActiveTimeEntryService } from '../../data/services/useActiveTimeEntryService';
import { useActiveTimer } from '../../data/services/useActiveTimer';
import { useTimeTrackingService } from '../../data/services/useTimeTrackingService';
import { useTaskService } from '../../data/services/useTaskService';
import { useCaseService } from '../../data/services/useCaseService';
import { Column, Paths, Row } from '../common';
import { appTokens } from '../common/Styles/appTokens';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { LinkButton } from './LinkButton';

const useStyles = makeStyles({
    root: {
        // borderRadius: tokens.borderRadiusXLarge,
        // // border: `1px solid ${appTokens.border.colors.subtle}`,
        // backgroundColor: appTokens.card.background,
        // boxSizing: 'border-box',
    },
    rootSmall: {
        padding: tokens.spacingVerticalXS,
    },
    rootMedium: {
        padding: tokens.spacingVerticalM,
    },
    rootLarge: {
        padding: tokens.spacingVerticalL,
    },
    // The Fluent spinner has no idle state, so freeze and dim it when no timer is running.
    spinnerIdle: {
        opacity: 0.35,
        '& *': {
            animationPlayState: 'paused',
        },
    },
    title: {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    captionText: {
        color: appTokens.text.colors.caption,
        whiteSpace: 'nowrap',
    },
    hoursText: {
        whiteSpace: 'nowrap',
    },
});

export type TimeTrackingTimerBarControlSize = 'small' | 'medium' | 'large';

type SizeSettings = {
    spinnerSize: SpinnerProps['size'];
    valueSize: TextProps['size'];
    captionSize: TextProps['size'];
    titleSize: TextProps['size'];
    buttonSize: ButtonProps['size'];
};

const sizeSettings: Record<TimeTrackingTimerBarControlSize, SizeSettings> = {
    small: {
        spinnerSize: 'small',
        valueSize: 400,
        captionSize: 200,
        titleSize: 200,
        buttonSize: 'medium',
    },
    medium: {
        spinnerSize: 'large',
        valueSize: 500,
        captionSize: 200,
        titleSize: 300,
        buttonSize: 'medium',
    },
    large: {
        spinnerSize: 'large',
        valueSize: 600,
        captionSize: 300,
        titleSize: 500,
        buttonSize: 'large',
    },
};

export type TimeTrackingTimerBarControlProps = {
    task?: Task;
    timeEntry?: TimeTrackingEntry;
    size?: TimeTrackingTimerBarControlSize;
    className?: string;
    onStartTimer?: () => void;
    onStopTimer?: () => void;
    onComplete?: (task: Task) => void;
    onClose?: (task: Task) => void;
    showClose?: boolean;
    showComplete?: boolean;
    showTaskTitle?: boolean;
    showCaseName?: boolean;
};

export const TimeTrackingTimerBarControl = (props: TimeTrackingTimerBarControlProps) => {
    const {
        task,
        timeEntry,
        size = 'medium',
        className,
        onStartTimer,
        onStopTimer,
        onComplete,
        onClose,
        showClose,
        showComplete,
        showTaskTitle = false,
        showCaseName = false,
    } = props;

    //Hooks
    const { activeTimeEntry } = useActiveTimeEntryService();
    const { hoursForEntry } = useActiveTimer();
    const timeEntryService = useTimeTrackingService();
    const taskService = useTaskService();
    const caseService = useCaseService();
    const navTo = useNavigate();

    //State
    const [isBusy, setIsBusy] = useState<boolean>(false);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const settings = sizeSettings[size];
    const rootClasses = mergeClasses(
        styles.root,
        size === 'small' && styles.rootSmall,
        size === 'medium' && styles.rootMedium,
        size === 'large' && styles.rootLarge,
    );

    //Logic
    const isActiveTimeEntry = () => {
        return (
            !!activeTimeEntry &&
            (activeTimeEntry.entryId === timeEntry?.entryId || (!!task?.taskId && activeTimeEntry.taskId === task.taskId))
        );
    };
    const isActive = isActiveTimeEntry();

    const entries = useMemo(() => {
        const allEntries = timeEntryService.timeEntries || [];
        let matched: TimeTrackingEntry[] = [];

        if (!!task?.taskId) {
            matched = allEntries.filter((entry) => entry.taskId === task.taskId);
        } else if (!!timeEntry?.entryId) {
            matched = allEntries.filter((entry) => entry.entryId === timeEntry.entryId);
        }

        if (matched.length === 0 && !!timeEntry) {
            matched = [timeEntry];
        }

        // The active entry in redux is more current than the cached list, so prefer it.
        if (!!activeTimeEntry && isActive) {
            matched = matched.filter((entry) => entry.entryId !== activeTimeEntry.entryId);
            matched = [...matched, activeTimeEntry];
        }

        return matched;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeEntryService.timeEntries, task?.taskId, timeEntry, activeTimeEntry, isActive]);

    const getEntryHours = (entry: TimeTrackingEntry) => {
        return hoursForEntry(entry);
    };
    const getTotalHours = () => {
        return entries.reduce((total, entry) => total + getEntryHours(entry), 0);
    };
    const formatHours = (hours: number) => {
        return `${Math.max(0, hours).toFixed(2)} hrs`;
    };
    const getHoursLabel = (hours: number) => {
        if (isActive) return 'In Progress';
        return hours > 0 ? 'Total hours' : 'Not Started';
    };
    const getStartTimerTooltip = () => {
        if (isActive) return 'This timer is active';
        return !!activeTimeEntry ? 'Another timer is active.' : 'Start Timer';
    };
    const canStart = () => !activeTimeEntry && !isBusy;
    const isTaskFinished = task?.status === TaskStatus.completed || task?.status === TaskStatus.closed;
    const canFinish = () => !!task && !isTaskFinished && !isBusy;
    const getCompleteTooltip = () => {
        if (!task) return 'No task to complete';
        if (isTaskFinished) return 'Task is already completed';
        return isActive ? 'Stop the timer and complete this task' : 'Complete this task';
    };
    const getCloseTooltip = () => {
        if (!task) return 'No task to close';
        if (isTaskFinished) return 'Task is already closed';
        return isActive ? 'Stop the timer and close this task' : 'Close this task';
    };

    const caseId = task?.caseId ?? timeEntry?.caseId;
    const displayTitle = task?.title || timeEntry?.description;
    const caseName = caseService.cases.find((c: Case) => c.caseId === caseId)?.caseName;

    //Click Events
    const timerTarget = task ?? timeEntry;
    const onClickStartTimer = async (e: React.MouseEvent) => {
        e.stopPropagation();

        await timeEntryService.startTimerAsync(timerTarget);
        !!onStartTimer && onStartTimer();
    };
    const onClickStopTimer = async (e: React.MouseEvent) => {
        e.stopPropagation();

        await timeEntryService.stopTimerAsync(timerTarget);
        !!onStopTimer && onStopTimer();
    };
    const onClickComplete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!task) return;

        setIsBusy(true);
        try {
            if (isActive) {
                await timeEntryService.stopTimerAsync(timerTarget);
                !!onStopTimer && onStopTimer();
            }

            await taskService.onComplete(task);
            !!onComplete && onComplete(task);
        } finally {
            setIsBusy(false);
        }
    };
    const onClickClose = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!task) return;

        setIsBusy(true);
        try {
            if (isActive) {
                await timeEntryService.stopTimerAsync(timerTarget);
                !!onStopTimer && onStopTimer();
            }

            await taskService.onClose(task);
            !!onClose && onClose(task);
        } finally {
            setIsBusy(false);
        }
    };
    const onClickCaseName = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!caseId) return;

        navTo(Paths.viewEditCase(caseId));
    };

    const totalHours = getTotalHours();
    // While a task's timer is running, the main readout tracks only the active entry and the total moves to the header.
    const showTotalInHeader = !!task?.taskId && isActive && !!activeTimeEntry;
    const displayedHours = showTotalInHeader && !!activeTimeEntry ? getEntryHours(activeTimeEntry) : totalHours;

    return (
        <Column className={className} borderRadius='8px' subtleBorder padding='px-2 py-2' noGap>
            <Row noGap justifyContent='space-between' noWrap>
                <Text size={200} className={commonStyles.text.subtleText}>Tracked Time</Text>
                {showTotalInHeader && (
                    <Text size={200} className={commonStyles.text.subtleText} data-testid='timerbar-header-total-time'>
                        {formatHours(totalHours)}
                    </Text>
                )}
            </Row>
            {showTaskTitle && !!displayTitle && (
                <Tooltip content={displayTitle} relationship='label'>
                    <Text
                        weight='semibold'
                        size={settings.titleSize}
                        className={styles.title}
                        data-testid='timerbar-title'
                    >
                        {displayTitle}
                    </Text>
                </Tooltip>
            )}
            {showCaseName && !!caseName && (
                <Row noGap noWrap data-testid='timerbar-case-name'>
                    <LinkButton text={caseName} subtle onClick={onClickCaseName} />
                </Row>
            )}
            <Row className={rootClasses} gap='medium' data-testid='timerbar-root'>
                <Column width='auto' align='center' vAlign='middle' padding='pr-2'>
                    <Spinner
                        size={settings.spinnerSize}
                        className={!isActive ? styles.spinnerIdle : undefined}
                        aria-label={isActive ? 'Timer running' : 'Timer stopped'}
                        data-testid='timerbar-spinner'
                    />
                </Column>

                <Column noGap width='auto' align='left' vAlign='middle'>
                    <Text
                        weight='semibold'
                        size={settings.valueSize}
                        className={styles.hoursText}
                        data-testid='timerbar-total-time'
                    >
                        {formatHours(displayedHours)}
                    </Text>
                    <Text size={settings.captionSize} className={styles.captionText} data-testid='timerbar-hours-label'>
                        {getHoursLabel(displayedHours)}
                    </Text>
                </Column>

                <Column vAlign='middle' padding='pl-2'>
                    <Row width='fit-content' gap='medium' noWrap>
                        {isActive ? (
                            <Button
                                appearance='secondary'
                                size={settings.buttonSize}
                                icon={
                                    <StopFilled
                                        color={
                                            !isActiveTimeEntry()
                                                ? tokens.colorNeutralForegroundDisabled
                                                : tokens.colorStatusDangerForeground1
                                        }
                                    />
                                }
                                disabled={isBusy}
                                onClick={onClickStopTimer}
                                data-testid='timerbar-stop-timer-button'
                            >
                                Stop
                            </Button>
                        ) : (
                            <Tooltip content={getStartTimerTooltip()} relationship='label'>
                                <Button
                                    appearance='secondary'
                                    size={settings.buttonSize}
                                    icon={
                                        <PlayFilled
                                            color={
                                                !!activeTimeEntry
                                                    ? tokens.colorNeutralForegroundDisabled
                                                    : tokens.colorStatusSuccessForeground1
                                            }
                                        />
                                    }
                                    disabled={!canStart()}
                                    onClick={onClickStartTimer}
                                    data-testid='timerbar-start-timer-button'
                                >
                                    Start
                                </Button>
                            </Tooltip>
                        )}

                        {showComplete && (
                            <Tooltip content={getCompleteTooltip()} relationship='label'>
                                <Button
                                    // className={commonStyles.button.successButton}
                                    appearance='secondary'
                                    size={settings.buttonSize}
                                    icon={<CheckmarkFilled color={tokens.colorStatusSuccessForeground1} />}
                                    disabled={!canFinish()}
                                    onClick={onClickComplete}
                                    data-testid='timerbar-finish-button'
                                ></Button>
                            </Tooltip>
                        )}

                        {showClose && (
                            <Tooltip content={getCloseTooltip()} relationship='label'>
                                <Button
                                    // className={commonStyles.button.dangerButton}
                                    appearance='secondary'
                                    size={settings.buttonSize}
                                    icon={<DismissFilled color={tokens.colorStatusDangerForeground1} />}
                                    disabled={!canFinish()}
                                    onClick={onClickClose}
                                    data-testid='timerbar-close-button'
                                ></Button>
                            </Tooltip>
                        )}
                    </Row>
                </Column>
            </Row>
        </Column>
    );
};
